import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Container, Typography, Box, Avatar, useMediaQuery } from '@mui/material';
import { DirectionsCar, Security, Speed, Check } from '@mui/icons-material';
import { useTenantTheme } from '../tenant/TenantThemeProvider';

const hexToRgba = (hex, alpha) => {
  const normalized = (hex || '').replace('#', '');
  const value = normalized.length === 3
    ? normalized.split('').map((char) => char + char).join('')
    : normalized;

  if (value.length !== 6) return `rgba(255, 255, 255, ${alpha})`;

  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const SlidesMain = () => {
  const history = useHistory();
  const [activeFeature, setActiveFeature] = useState(0);
  const [showContent, setShowContent] = useState(false);
  const { brandColors, tenantConfig } = useTenantTheme();
  const isLandscape = useMediaQuery('(orientation: landscape)');

  // Lista de características para mostrar en la animación
  const features = [
    { icon: <DirectionsCar />, text: "Inspección completa de vehículos" },
    { icon: <Security />, text: "Detección avanzada de daños" },
    { icon: <Speed />, text: "Procesamiento rápido de datos" },
    { icon: <Check />, text: "Reconocimiento preciso de documentos" }
  ];

  useEffect(() => {
    // Mostrar el contenido después de un breve retraso
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 1000);

    // Rotar las características cada 3 segundos
    const featureTimer = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % features.length);
    }, 3000);

    return () => {
      clearTimeout(timer);
      clearInterval(featureTimer);
    };
  }, [features.length]);

  const handleContinue = () => {
    history.push('/checklist');
  };

  return (
    <Container 
      className="slide-container" 
      maxWidth={false} 
      disableGutters
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: { xs: '16px', sm: '20px' },
        boxSizing: 'border-box',
        overflowY: 'auto',
        overflowX: 'hidden',
        background: `linear-gradient(180deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
        position: 'relative'
      }}
    >
      {/* Elemento decorativo superior */}
      <Box 
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '80px',
          height: '80px',
          background: brandColors.accent,
          borderRadius: '0 0 0 100%',
          zIndex: 1
        }}
      />
      
      {/* Elemento decorativo inferior */}
      <Box 
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '120px',
          height: '120px',
          background: hexToRgba(brandColors.primary, 0.3),
          borderRadius: '0 100% 0 0',
          zIndex: 1
        }}
      />
      
      {/* Sección superior con nombre de la aplicación */}
      <Box 
        className={`rac-logo-box ${showContent ? 'fade-in' : ''}`}
        sx={{ 
          width: '100%',
          textAlign: 'center',
          marginTop: { xs: isLandscape ? '12px' : '28px', sm: '40px' },
          zIndex: 2,
          opacity: showContent ? 1 : 0,
          transition: 'opacity 1s ease-in'
        }}
      >
        {tenantConfig.branding.logoUrl ? (
          <Box
            component="img"
            src={tenantConfig.branding.logoUrl}
            alt={`${tenantConfig.branding.name || 'FlowSure'} logo`}
            sx={{
              width: '100%',
              maxWidth: { xs: isLandscape ? 180 : 230, sm: 250 },
              height: 'auto',
              margin: '0 auto 10px',
              display: 'block'
            }}
          />
        ) : null}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#F9FCFF',
            letterSpacing: '0.04em',
            marginBottom: '10px',
            textShadow: '0 4px 10px rgba(0,0,0,0.25)',
            fontSize: { xs: isLandscape ? '1.35rem' : '2rem', sm: '2.125rem' }
          }}
        >
          {tenantConfig.branding.name || 'FlowSure'}
        </Typography>
      </Box>
      
      {/* Sección central con características rotativas */}
      <Box 
        className={`feature-box ${showContent ? 'fade-in' : ''}`}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: isLandscape ? '18px 20px' : '24px', sm: '30px' },
          marginY: { xs: isLandscape ? '12px' : '20px', sm: '20px' },
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          backdropFilter: 'blur(10px)',
          width: { xs: isLandscape ? '92%' : '85%', sm: '85%' },
          maxWidth: { xs: isLandscape ? '680px' : '400px', sm: '400px' },
          height: { xs: isLandscape ? '150px' : '200px', sm: '200px' },
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          opacity: showContent ? 1 : 0,
          transition: 'opacity 1s ease-in 0.5s',
          overflow: 'hidden'
        }}
      >
        {features.map((feature, index) => (
          <Box 
            key={index}
            sx={{
              display: 'flex',
              flexDirection: { xs: isLandscape ? 'row' : 'column', sm: 'column' },
              alignItems: 'center',
              justifyContent: 'center',
              position: 'absolute',
              width: '100%',
              height: '100%',
              opacity: activeFeature === index ? 1 : 0,
              transform: activeFeature === index ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.5s ease, transform 0.5s ease'
            }}
          >
            <Avatar 
              sx={{ 
                bgcolor: brandColors.accent,
                width: { xs: isLandscape ? 56 : 70, sm: 70 },
                height: { xs: isLandscape ? 56 : 70, sm: 70 },
                marginBottom: { xs: isLandscape ? 0 : 2, sm: 2 },
                marginRight: { xs: isLandscape ? 2 : 0, sm: 0 },
                boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
              }}
            >
              {feature.icon}
            </Avatar>
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#F9FCFF',
                textAlign: 'center',
                fontWeight: 'medium',
                marginTop: { xs: isLandscape ? 0 : '10px', sm: '10px' },
                fontSize: { xs: isLandscape ? '1rem' : '1.25rem', sm: '1.25rem' },
                maxWidth: { xs: isLandscape ? '70%' : '90%', sm: '90%' }
              }}
            >
              {feature.text}
            </Typography>
          </Box>
        ))}
      </Box>
      
      {/* Descripción */}
      <Typography 
        variant="body2" 
        className={showContent ? 'fade-in' : ''}
        sx={{ 
          color: '#F9FCFF',
          textAlign: 'center',
          maxWidth: { xs: isLandscape ? '580px' : '300px', sm: '300px' },
          margin: '0 auto',
          marginTop: { xs: isLandscape ? '10px' : '20px', sm: '20px' },
          opacity: showContent ? 0.9 : 0,
          transition: 'opacity 1s ease-in 0.8s',
          fontSize: { xs: isLandscape ? '0.82rem' : '0.875rem', sm: '0.875rem' },
          lineHeight: 1.35
        }}
      >
        Captura asistida para inspección vehicular con inteligencia artificial
      </Typography>
      
      {/* Botón de continuar */}
      <Button 
        variant="contained" 
        onClick={handleContinue} 
        className={`rac-continue-button ${showContent ? 'fade-in' : ''}`}
        sx={{ 
          width: '85%',
          maxWidth: { xs: isLandscape ? '320px' : '300px', sm: '300px' },
          marginTop: { xs: isLandscape ? '18px' : '30px', sm: '30px' },
          marginBottom: '20px',
          backgroundColor: brandColors.primary,
          color: '#F9FCFF',
          borderRadius: '50px',
          padding: '12px 30px',
          fontWeight: 'bold',
          letterSpacing: '0.1em',
          boxShadow: `0 4px 15px ${brandColors.primary}66`,
          opacity: showContent ? 1 : 0,
          transition: 'opacity 1s ease-in 1s, transform 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            backgroundColor: brandColors.secondary,
            transform: 'translateY(-3px)',
            boxShadow: `0 6px 20px ${brandColors.secondary}77`
          }
        }}
      >
        INICIAR
      </Button>
      
      {/* Copyright o versión */}
      <Typography 
        variant="caption" 
        className={showContent ? 'fade-in' : ''}
        sx={{ 
          color: 'rgba(255,255,255,0.5)',
          marginBottom: '5px',
          fontSize: '0.7rem',
          opacity: showContent ? 1 : 0,
          transition: 'opacity 1s ease-in 1.2s'
        }}
      >
        {new Date().getFullYear()}
      </Typography>
    </Container>
  );
};

export default SlidesMain;
